import { z } from 'zod'
import { idParam } from './id-validators'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

// Spec verzuim-workflow-accordering §4.1 — vijf statussen, canonieke
// presentation-volgorde voor filters/badges:
// GEMELD → ACTIEF → HERSTELD → LANGDURIG → AFGEKEURD.
// Flow-volgorde blijft GEMELD → (ACTIEF | AFGEKEURD) → BETER/LANGDURIG;
// "HERSTELD" heet in deze spec "BETER" in de UI, de enum-key blijft
// HERSTELD voor backwards-compat.
export const SICK_LEAVE_STATUS = ['GEMELD', 'ACTIEF', 'HERSTELD', 'LANGDURIG', 'AFGEKEURD'] as const
export type SickLeaveStatus = (typeof SICK_LEAVE_STATUS)[number]

export const SICK_LEAVE_REPORTED_BY = ['MEDEWERKER', 'TEAMLEIDER'] as const
export type SickLeaveReportedBy = (typeof SICK_LEAVE_REPORTED_BY)[number]

// Spec verzuim-workflow-accordering §3.5 — drie nieuwe acties: GEACCORDEERD,
// AFGEKEURD, VERVOLGACTIE_AFGEVINKT.
export const SICK_LEAVE_AUDIT_ACTIE = [
  'ZIEKMELDING_INGEDIEND',
  'GEACCORDEERD',
  'AFGEKEURD',
  'HERSTELMELDING',
  'STATUS_GEWIJZIGD',
  'VERVOLGACTIE_AFGEVINKT',
  'REINTEGRATIE_STAP_TOEGEVOEGD',
  'REINTEGRATIE_STAP_VOLTOOID',
  'NOTITIE_TOEGEVOEGD',
  'VERWIJDERD',
  // FO-087 (batch-20) — HR-gecureerde verzuim-tijdlijn.
  'TIJDLIJN_ITEM_TOEGEVOEGD',
  'TIJDLIJN_ITEM_GEWIJZIGD',
  'TIJDLIJN_ITEM_VERWIJDERD',
  // batch-40 uren-toeslag (FO-353 §3.2) — HR overrulet de samenloop-beslissing.
  'SAMENLOOP_OVERRULED',
  // FO-386 (batch-46) — poortwachter-termijnbewaking.
  'POORTWACHTER_GEMATERIALISEERD',
  'POORTWACHTER_STAP_VERVALLEN',
  'POORTWACHTER_DEADLINE_SIGNAAL',
  'DOSSIER_HEROPEND_SAMENTELLING',
] as const
export type SickLeaveAuditActie = (typeof SICK_LEAVE_AUDIT_ACTIE)[number]

// Spec verzuim-workflow-accordering §3.3 / §4.1 — checklist-types die TL bij
// accordering doorloopt. STANDAARD_VERVOLGACTIES is de subset die default
// gechecked verschijnt in de accordeer-modal; OVERIG wordt apart aangeboden
// als toggle met vrij tekstveld.
export const VERVOLGACTIE_TYPE = [
  'KLANT_GEINFORMEERD',
  'VERVANGING_GEREGELD',
  'PLANNING_AANGEPAST',
  'BEDRIJFSARTS_INGESCHAKELD',
  'OVERIG',
] as const
export type VervolgactieType = (typeof VERVOLGACTIE_TYPE)[number]

// Standaard-set die altijd getoond wordt in de accorderingsmodal (4 items
// uit spec §4.1 — OVERIG zit hier NIET in en wordt apart aangeboden).
export const STANDAARD_VERVOLGACTIES: VervolgactieType[] = [
  'KLANT_GEINFORMEERD',
  'VERVANGING_GEREGELD',
  'PLANNING_AANGEPAST',
  'BEDRIJFSARTS_INGESCHAKELD',
]

export const REINTEGRATIE_STAP_TYPE = [
  'EERSTE_GESPREK',
  'PROBLEEMANALYSE',
  'PLAN_VAN_AANPAK',
  'EVALUATIE',
  'EERSTEJAARSEVALUATIE',
  'WIA_AANVRAAG',
  // FO-386 (batch-46) — wettelijke UWV-ziekmelding week 42 (boeterisico).
  'UWV_ZIEKMELDING',
  'OVERIG',
] as const
export type ReintegratieStapType = (typeof REINTEGRATIE_STAP_TYPE)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const SICK_LEAVE_STATUS_LABELS: Record<SickLeaveStatus, string> = {
  GEMELD:    'Gemeld - wacht op accordering',
  ACTIEF:    'Actief',
  AFGEKEURD: 'Afgekeurd',
  HERSTELD:  'Hersteld',
  LANGDURIG: 'Langdurig',
}

// Labels voor vervolgacties (UI-tekst bij accorderingsmodal en lijst).
export const VERVOLGACTIE_TYPE_LABELS: Record<VervolgactieType, string> = {
  KLANT_GEINFORMEERD:        'Klant op de hoogte gesteld',
  VERVANGING_GEREGELD:       'Vervanging geregeld',
  PLANNING_AANGEPAST:        'Planning aangepast',
  BEDRIJFSARTS_INGESCHAKELD: 'Bedrijfsarts ingeschakeld',
  OVERIG:                    'Overig',
}

export const SICK_LEAVE_REPORTED_BY_LABELS: Record<SickLeaveReportedBy, string> = {
  MEDEWERKER:  'Medewerker',
  TEAMLEIDER:  'Teamleider',
}

export const SICK_LEAVE_AUDIT_ACTIE_LABELS: Record<SickLeaveAuditActie, string> = {
  ZIEKMELDING_INGEDIEND:        'Ziekmelding ingediend',
  GEACCORDEERD:                 'Geaccordeerd',
  AFGEKEURD:                    'Afgekeurd',
  HERSTELMELDING:               'Hersteld gemeld',
  STATUS_GEWIJZIGD:             'Status gewijzigd',
  VERVOLGACTIE_AFGEVINKT:       'Vervolgactie afgevinkt',
  REINTEGRATIE_STAP_TOEGEVOEGD: 'Re-integratie-stap toegevoegd',
  REINTEGRATIE_STAP_VOLTOOID:   'Re-integratie-stap voltooid',
  NOTITIE_TOEGEVOEGD:           'Notitie toegevoegd',
  VERWIJDERD:                   'Verwijderd',
  TIJDLIJN_ITEM_TOEGEVOEGD:     'Tijdlijn-item toegevoegd',
  TIJDLIJN_ITEM_GEWIJZIGD:      'Tijdlijn-item gewijzigd',
  TIJDLIJN_ITEM_VERWIJDERD:     'Tijdlijn-item verwijderd',
  SAMENLOOP_OVERRULED:          'Samenloop ziekte/verlof overruled',
  POORTWACHTER_GEMATERIALISEERD: 'Poortwachter-stappen aangemaakt',
  POORTWACHTER_STAP_VERVALLEN:  'Poortwachter-stap vervallen',
  POORTWACHTER_DEADLINE_SIGNAAL: 'Poortwachter-deadline gesignaleerd',
  DOSSIER_HEROPEND_SAMENTELLING: 'Dossier heropend (samentelling)',
}

export const REINTEGRATIE_STAP_TYPE_LABELS: Record<ReintegratieStapType, string> = {
  EERSTE_GESPREK:       'Eerste gesprek',
  PROBLEEMANALYSE:      'Probleemanalyse',
  PLAN_VAN_AANPAK:      'Plan van aanpak',
  EVALUATIE:            'Evaluatie',
  EERSTEJAARSEVALUATIE: 'Eerstejaarsevaluatie',
  WIA_AANVRAAG:         'WIA-aanvraag',
  UWV_ZIEKMELDING:      'Ziekmelding UWV',
  OVERIG:               'Overig',
}

// Standaard Wet Verbetering Poortwachter timeline (week-offset vanaf startDate).
export const REINTEGRATIE_STAP_DEFAULT_WEEK: Record<ReintegratieStapType, number | null> = {
  EERSTE_GESPREK:       1,
  PROBLEEMANALYSE:      6,
  PLAN_VAN_AANPAK:      8,
  EVALUATIE:            0, // periodiek, geen vaste week
  EERSTEJAARSEVALUATIE: 52,
  WIA_AANVRAAG:         91,
  UWV_ZIEKMELDING:      42, // wettelijke UWV-ziekmelding
  OVERIG:               null,
}

// Status badge variant voor StatusBadge-component (spec verzuim-workflow-accordering §4.1).
export const SICK_LEAVE_STATUS_BADGE_VARIANT: Record<
  SickLeaveStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  GEMELD:    'info',       // blauw — wacht op accordering
  ACTIEF:    'warning',    // oranje — loopt
  AFGEKEURD: 'secondary',  // grijs — niet geaccordeerd
  HERSTELD:  'success',    // groen — afgesloten
  LANGDURIG: 'danger',     // rood — vereist begeleiding
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

export interface SickLeaveListRowDTO {
  id: string
  employeeId: string
  employeeName: string         // computed: firstName + lastName
  employeeNumber: string
  startDate: string            // ISO yyyy-MM-dd
  endDate: string | null
  status: SickLeaveStatus
  reportedBy: SickLeaveReportedBy
  isLangdurig: boolean
  aantalDagen: number          // computed via utils
  reintegratieStappenAantal: number  // count voltooid + open
  klachten: string | null      // truncated tot 80 chars voor lijst
  // Spec verzuim-workflow-accordering §4.2 — accordering-info voor lijst.
  geaccordeerdDoorNaam: string | null
  aantalVervolgactiesOpen: number
  // Afkeur-info — gebruikt door het portal-status-banner (spec §7.4).
  // Bewust meegestuurd op de lijst-shape zodat de medewerker geen extra
  // detail-fetch hoeft te doen om de afkeurreden te zien. Backoffice
  // negeert deze velden in de lijst-tabel — daar staat een aparte kolom
  // "Geaccordeerd door" en de afkeurreden is alleen in de detail-banner.
  afgekeurdDoorNaam: string | null
  afkeurReden: string | null
}

/** Eén rij uit de VerzuimDag-ledger, voor read-only weergave op de detail-pagina. */
export interface VerzuimDagDTO {
  datum: string         // ISO yyyy-MM-dd
  uren: number          // Number(Decimal)
  volledig: boolean
  startTijd: string | null
  eindTijd: string | null
}

export interface SickLeaveDetailDTO {
  id: string
  employeeId: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string
    functionTitle: string | null
  }
  startDate: string
  endDate: string | null
  status: SickLeaveStatus
  reportedBy: SickLeaveReportedBy
  reportedById: string
  reportedByName: string | null  // null als de actor niet meer bestaat
  klachten: string | null
  notities: string | null
  meldingLeidinggevende: boolean
  meldingKlant: boolean
  isLangdurig: boolean
  langdurigSinds: string | null
  aantalDagen: number
  reintegratieStappen: ReintegratieStapDTO[]
  audits: SickLeaveAuditDTO[]
  createdAt: string
  updatedAt: string
  // Spec verzuim-workflow-accordering §4.2 — accordering- en afkeurinformatie.
  geaccordeerdDoor: { id: string; naam: string } | null
  geaccordeerdOp: string | null
  accorderingOpmerking: string | null
  afgekeurdDoor: { id: string; naam: string } | null
  afgekeurdOp: string | null
  afkeurReden: string | null
  vervolgacties: VerzuimVervolgactieDTO[]
  /** Per-dag uren-ledger, gesorteerd op datum asc. Leeg array als geen data. */
  verzuimDagen: VerzuimDagDTO[]
  /**
   * FO-087 — HR-gecureerde verzuim-tijdlijn (contactmomenten/afspraken/notities),
   * gesorteerd op `datum` desc (nieuwste boven). Alleen `deletedAt: null`.
   * Vertrouwelijk: nooit naar portal/klant.
   */
  tijdlijnItems: VerzuimTijdlijnItemDTO[]
  /**
   * FO-387 — verzuimfrequentie-blok (aantal erkende meldingen + verzuim% + open
   * signaal). Alleen aanwezig voor een VERZUIM.canView-viewer; `null` bij een
   * geredigeerde (PLANNER) view (FO-086-redactie).
   */
  frequentieBlok: VerzuimFrequentieBlokDTO | null
}

// Spec verzuim-workflow-accordering §4.2 — DTO voor een vervolgactie. `label`
// wordt vastgehouden op moment van aanmaken zodat herlabelen van enum-defaults
// bestaande rijen niet retroactief wijzigt.
export interface VerzuimVervolgactieDTO {
  id: string
  type: VervolgactieType
  label: string
  afgevinkt: boolean
  opmerking: string | null
  afgevinktDoor: { id: string; naam: string } | null
  afgevinktOp: string | null
}

export interface ReintegratieStapDTO {
  id: string
  type: ReintegratieStapType
  geplandOp: string
  uitgevoerdOp: string | null
  notities: string | null
  bestandsRef: string | null
  createdAt: string
  // FO-386 (batch-46) — poortwachter-materialisatie + deadlinebewaking.
  /** Berekende wettelijke deadline (yyyy-MM-dd), null op handmatige/legacy stappen. */
  deadlineOp: string | null
  /** Wettelijke termijn (UWV wk42/WIA) — T-0-signaal is mandatory HOOG. */
  wettelijk: boolean
  /** Gezet bij herstel: stap vervallen (yyyy-MM-dd); geen hard delete. */
  vervallenOp: string | null
  /** Herkomst-sjabloonstap (snapshot); null = handmatig toegevoegd. */
  sjabloonStapId: string | null
}

export interface SickLeaveAuditDTO {
  id: string
  actie: SickLeaveAuditActie
  actor: string
  actorName: string | null
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

// ─── FO-087 — Verzuim-tijdlijn (HR-gecureerde contactmomenten/afspraken) ──────
//
// Per ziekmelding legt HR contactmomenten, afspraken en notities vast (vrije
// tekst, datum, auteur). Vertrouwelijk t.o.v. de medewerker (geen portal/klant).
// BR #46: GEEN medische diagnose-details in `tekst` (administratieve historie).

export const VERZUIM_TIJDLIJN_ITEM_TYPE = ['CONTACTMOMENT', 'AFSPRAAK', 'NOTITIE'] as const
export type VerzuimTijdlijnItemType = (typeof VERZUIM_TIJDLIJN_ITEM_TYPE)[number]

export const VERZUIM_TIJDLIJN_ITEM_TYPE_LABELS: Record<VerzuimTijdlijnItemType, string> = {
  CONTACTMOMENT: 'Contactmoment',
  AFSPRAAK:      'Afspraak',
  NOTITIE:       'Notitie',
}

export interface VerzuimTijdlijnItemDTO {
  id: string
  type: VerzuimTijdlijnItemType
  datum: string              // ISO yyyy-MM-dd (HR-gekozen kalenderdatum)
  tekst: string
  auteurId: string
  auteurNaam: string | null  // null als de auteur niet meer bestaat
  /** Alleen relevant voor type AFSPRAAK: ISO yyyy-MM-dd, null = open afspraak. */
  afgerondOp: string | null
  createdAt: string          // ISO
  updatedAt: string          // ISO
}

// Gedeeld request-schema (CLAUDE.md: API + form valideren met dezelfde bron).
const verzuimTijdlijnDatum = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (YYYY-MM-DD)')

const verzuimTijdlijnTekst = z
  .string()
  .trim()
  .min(1, 'Tekst is verplicht')
  .max(2000, 'Tekst mag maximaal 2000 tekens bevatten')

/** Body voor POST /sick-leave/:id/tijdlijn-item. Auteur = server-side. */
export const verzuimTijdlijnItemCreateSchema = z.object({
  type: z.enum(VERZUIM_TIJDLIJN_ITEM_TYPE),
  datum: verzuimTijdlijnDatum,
  tekst: verzuimTijdlijnTekst,
})
export type VerzuimTijdlijnItemCreateInput = z.infer<typeof verzuimTijdlijnItemCreateSchema>

/**
 * Body voor PATCH /sick-leave/:id/tijdlijn-item/:itemId. Tekst/datum corrigeren;
 * voor AFSPRAAK `afgerondOp` zetten ("markeer als afgerond") of wissen
 * (`null` = heropenen). Alle velden optioneel, maar minstens één vereist.
 */
export const verzuimTijdlijnItemUpdateSchema = z
  .object({
    datum: verzuimTijdlijnDatum.optional(),
    tekst: verzuimTijdlijnTekst.optional(),
    afgerondOp: verzuimTijdlijnDatum.nullable().optional(),
  })
  .refine(
    (v) => v.datum !== undefined || v.tekst !== undefined || v.afgerondOp !== undefined,
    { message: 'Geef minstens één veld om te wijzigen' },
  )
export type VerzuimTijdlijnItemUpdateInput = z.infer<typeof verzuimTijdlijnItemUpdateSchema>

export interface SickLeaveStatsDTO {
  periode: { start: string; end: string }      // ISO dates
  verzuimpercentage: number                     // 0-100, 1 decimaal
  ziekteDagen: number                           // werkdagen ziek totaal in periode
  beschikbareWerkdagen: number
  actieveZiekmeldingen: number
  langdurigeZiekmeldingen: number
  herstelMeldingenInPeriode: number
  // Spec verzuim-workflow-accordering §7.1 — KPI-tile "Wacht op accordering".
  wachtOpAccordering: number
}

// ─── Zod schemas (input-validatie voor accordering-workflow) ─────────────────

// Spec verzuim-workflow-accordering §4.3 — body voor PATCH /sick-leave/:id/accordeer.
// `vervolgacties` is optioneel; ontbreken = TL slaat de checklist over (geen
// items aangemaakt). Bij OVERIG moet `label` gevuld zijn — server-side
// gehandhaafd in PR-B2.
export const accordeerVerzuimSchema = z.object({
  opmerking: z.string().trim().max(2000).optional(),
  vervolgacties: z
    .array(
      z.object({
        type: z.enum(VERVOLGACTIE_TYPE),
        label: z.string().trim().max(200),
        afgevinkt: z.boolean().default(false),
        opmerking: z.string().trim().max(500).optional(),
      }),
    )
    .optional(),
})

export type AccordeerVerzuimInput = z.infer<typeof accordeerVerzuimSchema>

// Spec verzuim-workflow-accordering §4.3 — body voor PATCH /sick-leave/:id/afkeuren.
// Reden is verplicht (minimaal 5 tekens) zodat medewerker context heeft in
// de notificatie.
export const keurVerzuimAfSchema = z.object({
  reden: z.string().trim().min(5, 'Reden is verplicht (minimaal 5 tekens)').max(2000),
})

export type KeurVerzuimAfInput = z.infer<typeof keurVerzuimAfSchema>

// Spec verzuim-workflow-accordering §4.3 — body voor
// PATCH /sick-leave/:id/vervolgacties/:vaId (toggle + optioneel opmerking).
export const vervolgactieAfvinkenSchema = z.object({
  afgevinkt: z.boolean(),
  opmerking: z.string().trim().max(500).optional(),
})

export type VervolgactieAfvinkenInput = z.infer<typeof vervolgactieAfvinkenSchema>

// Audit-fixes 2026-06-10 (Deel 4a) — body voor
// POST /sick-leave/:id/vervolgacties: los een vervolgactie toevoegen vanaf de
// Vervolgacties-tab, buiten de accordeer-modal om (bv. een actie die pas later
// nodig blijkt, of wanneer de checklist bij accordering is overgeslagen).
// Zelfde veldregels als de checklist-items in `accordeerVerzuimSchema`; label
// is hier altijd verplicht. Voor standaard-types snapshot de server alsnog het
// vaste label uit VERVOLGACTIE_TYPE_LABELS (consistent met de accordeer-route).
// `opmerking` is nullable: de read-DTO geeft `opmerking: string | null` terug.
export const vervolgactieToevoegenSchema = z.object({
  type: z.enum(VERVOLGACTIE_TYPE).default('OVERIG'),
  label: z.string().trim().min(1, 'Label is verplicht').max(200),
  opmerking: z.string().trim().max(500).nullable().optional(),
})

export type VervolgactieToevoegenInput = z.infer<typeof vervolgactieToevoegenSchema>

// Spec verzuim-workflow-accordering §4.3 — body voor
// PUT /instellingen/verzuim/vaste-contactpersonen.
export const verzuimVasteContactpersonenSchema = z.object({
  // idParam i.p.v. .cuid(): 81% van de prod-users heeft een legacy-UUID-id;
  // .cuid() weigerde die als contactpersoon (zelfde klasse als de
  // werkbon-cuid2-bug, zie id-validators.ts).
  userIds: z.array(idParam('userId')).min(1, 'Minimaal 1 contactpersoon'),
})

export type VerzuimVasteContactpersonenInput = z.infer<typeof verzuimVasteContactpersonenSchema>

// ─── Loondoorbetaling-config (verzuim fase 5 — payroll) ──────────────────────
//
// CAO Metaal en Techniek: loondoorbetaling bij ziekte loopt in trappen
// (standaard 100% jaar 1, 90% jaar 2, 70% daarna). De trappen + het aantal
// wachtdagen zijn configureerbaar zodat een afwijkend CAO-/bedrijfsbeleid geen
// codewijziging vereist. Gevoed in de payroll-export als ZIEK_100/90/70-
// loondoorbetalingscomponenten (geen hardcoded percentages, geen verborgen
// defaults — de cascade instelling → DEFAULT_DOORBETALING_TIERS is expliciet in
// `getDoorbetalingConfig`).

// Eén trap: tot-en-met week `totWeek` (inclusief) geldt percentage `pct`.
// `totWeek: null` = open einde (alles daarna). pct is een heel percentage 0..100.
export const doorbetalingTierSchema = z.object({
  totWeek: z.number().int().positive().nullable(),
  pct: z.number().min(0).max(100),
})

export type DoorbetalingTierInput = z.infer<typeof doorbetalingTierSchema>

// PUT-body voor /instellingen/verzuim/doorbetaling. Minimaal 1 trap; de laatste
// trap MOET `totWeek: null` hebben (open einde) zodat er geen gat valt na de
// hoogste begrensde week. De trappen worden in volgorde geëvalueerd
// (`doorbetalingPctVoorWeek` pakt de eerste match), dus de begrensde `totWeek`-
// waarden moeten strikt oplopend zijn — anders maskeert een vroege trap een
// latere en betaalt het verkeerde percentage uit. De superRefine handhaaft
// beide invarianten zodat een onjuist gesorteerde of open-einde-loze config niet
// stilzwijgend mis-uitbetaalt.
export const verzuimDoorbetalingSchema = z.object({
  tiers: z
    .array(doorbetalingTierSchema)
    .min(1, 'Minimaal 1 doorbetaling-trap')
    .superRefine((tiers, ctx) => {
      // 1. Strikt oplopende begrensde `totWeek`-waarden, in array-volgorde.
      //    `null` (open einde) hoort alleen als laatste element voor te komen.
      let vorigeTotWeek: number | null = null
      for (let i = 0; i < tiers.length; i++) {
        const totWeek = tiers[i].totWeek
        if (totWeek === null) {
          // Een open einde mag uitsluitend de laatste trap zijn.
          if (i !== tiers.length - 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [i, 'totWeek'],
              message: 'Een open einde (totWeek: null) mag alleen de laatste trap zijn',
            })
          }
          continue
        }
        if (vorigeTotWeek !== null && totWeek <= vorigeTotWeek) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, 'totWeek'],
            message: 'totWeek-waarden moeten strikt oplopend zijn',
          })
        }
        vorigeTotWeek = totWeek
      }

      // 2. Precies één open-einde-trap (totWeek: null), en die moet de laatste zijn.
      const openEindeCount = tiers.filter((t) => t.totWeek === null).length
      if (openEindeCount === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [tiers.length - 1, 'totWeek'],
          message: 'De laatste trap moet een open einde hebben (totWeek: null)',
        })
      } else if (openEindeCount > 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [tiers.length - 1, 'totWeek'],
          message: 'Slechts één trap mag een open einde (totWeek: null) hebben',
        })
      }
    }),
  wachtdagen: z.object({
    aantal: z.number().int().min(0).max(5),
  }),
  // Werkgeverslasten/opslag (verzuim spec 30 #5): de verzuimkosten-kostenbasis is
  // loondoorbetaling + werkgeverslasten (sociale premies, pensioen, vakantiegeld-
  // opbouw, etc.). Dit is een opslag-percentage bovenop de loondoorbetaling:
  // 30 = +30%. Geen verborgen default (CLAUDE.md) — HR stelt het bewust in; tot
  // die tijd 0 (geen verzonnen percentage), zodat totaleKosten == loondoorbetaling
  // tot HR een waarde invult. Max 500% als ruime, niet-betekenisvolle bovengrens.
  werkgeverslastenOpslag: z.number().min(0).max(500).default(0),
})

export type VerzuimDoorbetalingInput = z.infer<typeof verzuimDoorbetalingSchema>

// ─── DTOs voor instellingen-endpoint ─────────────────────────────────────────

// Spec verzuim-workflow-accordering §5.6 — GET /instellingen/verzuim levert
// resolved user-info (id + naam) zodat backoffice UI direct kan renderen.
export interface VerzuimInstellingenDTO {
  vasteContactpersonen: { id: string; naam: string }[]
}

// Constante key-namen voor de VerzuimInstelling-tabel (`key`-veld). Single
// source of truth zodat seed + API beide dezelfde sleutel gebruiken.
export const VERZUIM_INSTELLING_KEYS = {
  VASTE_CONTACTPERSONEN: 'VASTE_CONTACTPERSONEN',
  // Loondoorbetaling-config (verzuim fase 5): trappen + wachtdagen. Twee aparte
  // rijen zodat een latere uitbreiding van één van beide geen schema-migratie
  // vereist.
  DOORBETALING_TIERS: 'DOORBETALING_TIERS',
  WACHTDAGEN: 'WACHTDAGEN',
  // Werkgeverslasten-opslag (verzuim spec 30 #5): percentage bovenop de
  // loondoorbetaling voor de verzuimkosten-kostenbasis (30 = +30%).
  WERKGEVERSLASTEN_OPSLAG: 'WERKGEVERSLASTEN_OPSLAG',
  // Spec 35 — link naar een extern verzuim-/poortwachterportaal (optioneel).
  // Lege string = knop verborgen; anders verplicht https://-URL.
  // Batch-7 fx.3: dit is het CVB-portaal (ziekmeldingen op of na 01-01-2026).
  EXTERN_PORTAAL: 'EXTERN_PORTAAL',
  // Batch-7 fx.3 — tweede portaal: a.s.r. verzuim (ziekmeldingen van vóór
  // 01-01-2026). Zelfde value-shape als EXTERN_PORTAAL ({ label, url }).
  EXTERN_PORTAAL_ASR: 'EXTERN_PORTAAL_ASR',
  // FO-302 (batch-32) — meldingshorizon (dagen) voor open-einde-verzuim: hoever
  // vooruit klant-planning in de afwezigheidsmail/-kaart valt zolang de
  // ziekmelding geen einddatum heeft. Value-shape { dagen: number }, default 14.
  MELDINGSHORIZON_DAGEN: 'MELDINGSHORIZON_DAGEN',
  // FO-386 (batch-46) — samentellingsgrens (kalenderdagen): meldt dezelfde
  // medewerker zich binnen dit venster na herstel opnieuw ziek, dan telt de
  // nieuwe melding samen met het bestaande dossier (art. 7:629 lid 10 BW).
  // Value-shape { dagen: number }, default 28.
  POORTWACHTER_SAMENTELLING_DAGEN: 'POORTWACHTER_SAMENTELLING_DAGEN',
  // FO-387 (batch-46) — frequent-verzuim-drempel. Value-shape
  // { actief: boolean, drempelAantal: number, periodeMaanden: number }.
  FREQUENT_VERZUIM: 'FREQUENT_VERZUIM',
} as const

/** FO-302 — default meldingshorizon (dagen) bij open-einde-verzuim. */
export const VERZUIM_MELDINGSHORIZON_DAGEN_DEFAULT = 14

/** FO-302 — request-body voor het instellen van de meldingshorizon (1..90 dagen). */
export const verzuimMeldingshorizonSchema = z.object({
  dagen: z.number().int().min(1).max(90),
})
export type VerzuimMeldingshorizonInput = z.infer<typeof verzuimMeldingshorizonSchema>

export type VerzuimInstellingKey = (typeof VERZUIM_INSTELLING_KEYS)[keyof typeof VERZUIM_INSTELLING_KEYS]

// Spec 35 — instelling voor extern poortwachter-/verzuimportaal.
// url: lege string = knop niet tonen; anders verplicht https://-URL.
export const verzuimExternPortaalSchema = z.object({
  label: z.string().min(1).max(100),
  url: z.string().refine(
    (v) => v === '' || v.startsWith('https://'),
    { message: 'URL moet met https:// beginnen' },
  ),
})
export type VerzuimExternPortaalInput = z.infer<typeof verzuimExternPortaalSchema>

export interface VerzuimExternPortaalDTO {
  label: string
  url: string
}

/**
 * Batch-7 fx.3 — beide verzekeraar-portalen in één response
 * (`GET /instellingen/verzuim/extern-portalen`).
 */
export interface VerzuimExternPortalenDTO {
  /** CVB-portaal: ziekmeldingen op of na de datumgrens. */
  cvb: VerzuimExternPortaalDTO
  /** a.s.r.-portaal: ziekmeldingen van vóór de datumgrens. */
  asr: VerzuimExternPortaalDTO
}

/**
 * Datumgrens tussen de twee verzekeraar-portalen: ziek geworden vóór
 * 01-01-2026 → a.s.r. verzuim; op of na 01-01-2026 → CVB verzuim.
 * (NL-kalenderdag, yyyy-mm-dd.)
 */
export const VERZUIM_PORTAAL_DATUMGRENS_YMD = '2026-01-01'

/**
 * Defaults voor de twee portalen. De URL's zijn instelbaar via
 * `/instellingen/verzuim` (VerzuimInstelling-laag); deze defaults gelden
 * zolang er geen (geldige) rij staat en worden door de seed + backfill
 * gebruikt. Lege url in een bestaande rij = knop bewust verborgen.
 *
 * NB: het CVB-label hoort bij het Mijn SVB-portaal (svb.nl) — zie de
 * verificatievraag in de batch-7 STATUS; een labelwijziging is een
 * 1-regel-fix in de instellingen-UI of hier.
 */
export const VERZUIM_EXTERN_PORTAAL_DEFAULTS: VerzuimExternPortalenDTO = {
  cvb: {
    label: 'CVB verzuim',
    url: 'https://www.svb.nl/nl/uitleg-mijnsvb/inloggen-op-mijn-svb',
  },
  asr: {
    // Batch-15 FO-024: het a.s.r.-werkgeverportaal-login (stabiele basis die zelf
    // zijn OAuth start) — niet de algemene asr.nl-inlog of een eenmalige
    // OAuth-link met nonce/state. Instelbaar via /instellingen/verzuim.
    label: 'a.s.r. verzuim',
    url: 'https://login.werkgever.asr.nl/werkgever/login/',
  },
}

// ─── Verzuimkosten-rapportage (fase 6) ───────────────────────────────────────
//
// GET /api/v1/verzuimkosten?vanaf=&totEnMet=[&bvId=]
// Retourneert per medewerker: ziekteUren, ziektedagen, verzuimPct en
// loondoorbetaling (kosten werkgever). Response is een platte lijst van
// medewerker-rijen; de UI groepeert per BV/entiteit.

export const verzuimkostenQuerySchema = z.object({
  vanaf: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)'),
  totEnMet: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldig datum-formaat (YYYY-MM-DD)'),
  bvId: z.string().min(1).optional(),
})

export type VerzuimkostenQuery = z.infer<typeof verzuimkostenQuerySchema>

/** Eén rij in de verzuimkosten-rapportage (per medewerker). */
export interface VerzuimkostenRijDTO {
  employeeId: string
  employeeNumber: string
  naam: string              // firstName + lastName
  bvId: string | null
  bvNaam: string | null
  ziekteUren: number        // Decimaal — som van VerzuimDag.uren in de periode
  ziektedagen: number       // Aantal unieke datum-waarden in de periode
  verzuimPct: number | null // null als beschikbareUren = 0 (geen contract-info)
  loondoorbetaling: number  // EUR (werkgeverskosten loondoorbetaling)
  // Verzuim spec 30 #5: werkgeverslasten/opslag bovenop de loondoorbetaling
  // (loondoorbetaling × opslagPct / 100) en de totale kosten (som van beide).
  werkgeverslasten: number  // EUR (opslag bovenop loondoorbetaling)
  totaleKosten: number      // EUR (loondoorbetaling + werkgeverslasten)
}

/** Subtotaal per BV/entiteit. */
export interface VerzuimkostenBvSubtotaalDTO {
  bvId: string | null
  bvNaam: string | null
  ziekteUren: number
  ziektedagen: number
  loondoorbetaling: number
  werkgeverslasten: number
  totaleKosten: number
}

/** Volledige response-body van GET /api/v1/verzuimkosten. */
export interface VerzuimkostenResponseDTO {
  data: VerzuimkostenRijDTO[]
  meta: {
    periode: { vanaf: string; totEnMet: string }
    /** Subtotalen per BV (afgeleid van `data`). */
    bvSubtotalen: VerzuimkostenBvSubtotaalDTO[]
    /** Totalen over alle BV's. */
    totaal: {
      ziekteUren: number
      ziektedagen: number
      loondoorbetaling: number
      werkgeverslasten: number
      totaleKosten: number
    }
  }
}

// ── batch-40 uren-toeslag — FO-353 samenloop ziekte/verlof ───────────────────

/**
 * De samenloop-uitkomst per kalenderdag (`VerzuimDag.samenloop`).
 *
 *   GEEN                   - geen goedgekeurd verlof op deze dag (de norm)
 *   ZIEKTE_WINT            - ziek geworden tijdens goedgekeurd verlof: dag =
 *                            ziekte, verlofuren niet genoten en teruggeboekt
 *   VERDRONGEN_DOOR_VERLOF - verlof aangevraagd tijdens lopende ziekte: dag =
 *                            verlof; de ziekte loopt door maar levert 0 uren
 */
export const VERZUIM_DAG_SAMENLOOP = ['GEEN', 'ZIEKTE_WINT', 'VERDRONGEN_DOOR_VERLOF'] as const
export type VerzuimDagSamenloopKey = (typeof VERZUIM_DAG_SAMENLOOP)[number]

export const SAMENLOOP_LABELS: Record<VerzuimDagSamenloopKey, string> = {
  GEEN: 'Geen samenloop',
  ZIEKTE_WINT: 'Telt als ziekte',
  VERDRONGEN_DOOR_VERLOF: 'Telt als verlof',
}

/**
 * FO-353 §3.2 / §6 — HR overrulet de automatische beslissing voor één of meer
 * dagen. Reden verplicht (min. 3 tekens): de regels zijn de default, niet een
 * dictaat, en een afwijking moet later navolgbaar zijn.
 *
 * Gedeeld schema: de API valideert ermee en het form valideert er client-side
 * mee vóór verzenden, zodat het form niets kan sturen dat de API weigert.
 */
export const samenloopOverrideSchema = z.object({
  /** De dagen die je overrulet (yyyy-MM-dd). */
  datums: z.array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Datum moet yyyy-MM-dd zijn')).min(1, 'Kies minstens één dag'),
  /** De gewenste uitkomst. `GEEN` bestaat hier niet: je overrulet een samenloop. */
  soort: z.enum(['ZIEKTE_WINT', 'VERDRONGEN_DOOR_VERLOF']),
  reden: z.string().trim().min(3, 'Geef een reden van minimaal 3 tekens'),
})
export type SamenloopOverrideInput = z.infer<typeof samenloopOverrideSchema>

/** Eén samenloop-dag zoals het verzuimdossier hem toont (§8). */
export interface SamenloopDagDTO {
  /** yyyy-MM-dd */
  datum: string
  soort: VerzuimDagSamenloopKey
  /** Roosteruren van die dag. */
  uren: number
  /** De verlofaanvraag waarmee de dag samenloopt. */
  leaveRequestId: string | null
  /** Gevuld zodra HR de automatische uitkomst heeft overruled. */
  overrideDoor: string | null
  overrideOp: string | null
  overrideReden: string | null
}

// ─── FO-386 — Poortwachter-termijnen sjabloon + materialisatie ───────────────

/** FO-386 §5 — samentellingsgrens (kalenderdagen) na herstel; default 28. */
export const POORTWACHTER_SAMENTELLING_DAGEN_DEFAULT = 28

/** Body voor PUT /instellingen/verzuim/poortwachter-samentelling (1..90 dagen). */
export const poortwachterSamentellingSchema = z.object({
  dagen: z.number().int().min(1).max(90),
})
export type PoortwachterSamentellingInput = z.infer<typeof poortwachterSamentellingSchema>

/** Eén beheerde poortwachter-sjabloonstap (read-DTO). */
export interface PoortwachterStapSjabloonDTO {
  id: string
  reintegratieStapType: ReintegratieStapType
  label: string
  /** Week-offset t.o.v. de eerste ziektedag (SickLeave.startDate). */
  weekVanaf: number
  /** Optioneel bereik-einde (bv. WIA wk 87-91); null = één vaste week. */
  weekTot: number | null
  wettelijk: boolean
  /** Voor de rolling evaluatie (elke N weken); null = eenmalig. */
  herhaalIntervalWeken: number | null
  volgorde: number
  actief: boolean
}

const poortwachterStapSjabloonFields = {
  reintegratieStapType: z.enum(REINTEGRATIE_STAP_TYPE),
  label: z.string().trim().min(1, 'Label is verplicht').max(200),
  // 0..520 weken (10 jaar) als ruime, niet-betekenisvolle bovengrens.
  weekVanaf: z.number().int().min(0).max(520),
  weekTot: z.number().int().min(0).max(520).nullable().optional(),
  wettelijk: z.boolean(),
  herhaalIntervalWeken: z.number().int().positive().max(104).nullable().optional(),
  volgorde: z.number().int().min(0).optional(),
  actief: z.boolean().optional(),
}

/** Body voor POST /instellingen/verzuim/poortwachter-sjabloon. */
export const poortwachterStapSjabloonCreateSchema = z
  .object(poortwachterStapSjabloonFields)
  .refine((v) => v.weekTot == null || v.weekTot >= v.weekVanaf, {
    message: 'weekTot moet groter of gelijk aan weekVanaf zijn',
    path: ['weekTot'],
  })
export type PoortwachterStapSjabloonCreateInput = z.infer<
  typeof poortwachterStapSjabloonCreateSchema
>

/**
 * Body voor PATCH /instellingen/verzuim/poortwachter-sjabloon/:id. Alle velden
 * optioneel; minstens één vereist. De weekTot >= weekVanaf-invariant wordt
 * server-side tegen de bestaande waarde getoetst (partial update).
 */
export const poortwachterStapSjabloonUpdateSchema = z
  .object({
    reintegratieStapType: poortwachterStapSjabloonFields.reintegratieStapType.optional(),
    label: poortwachterStapSjabloonFields.label.optional(),
    weekVanaf: poortwachterStapSjabloonFields.weekVanaf.optional(),
    weekTot: poortwachterStapSjabloonFields.weekTot,
    wettelijk: z.boolean().optional(),
    herhaalIntervalWeken: poortwachterStapSjabloonFields.herhaalIntervalWeken,
    volgorde: poortwachterStapSjabloonFields.volgorde,
    actief: poortwachterStapSjabloonFields.actief,
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'Geef minstens één veld om te wijzigen',
  })
export type PoortwachterStapSjabloonUpdateInput = z.infer<
  typeof poortwachterStapSjabloonUpdateSchema
>

// ─── FO-387 — Frequent-verzuim-signaal ───────────────────────────────────────

/**
 * FO-387 §3.1 — frequent-verzuim-drempel. Detectie start bewust UIT (CUTOVER-
 * veiligheid: HR zet 'm aan na het instellen van de drempel, zodat de eerste
 * tick geen retroactieve HR-berg oplevert). Drempel 3 erkende meldingen / 12 mnd.
 */
export const FREQUENT_VERZUIM_DEFAULTS = {
  actief: false,
  drempelAantal: 3,
  periodeMaanden: 12,
} as const

export interface FrequentVerzuimConfig {
  actief: boolean
  drempelAantal: number
  periodeMaanden: number
}

/** Body voor PUT /instellingen/verzuim/frequent-verzuim. */
export const frequentVerzuimConfigSchema = z.object({
  actief: z.boolean(),
  // >= 2 want 1 melding is per definitie geen patroon.
  drempelAantal: z.number().int().min(2).max(20),
  periodeMaanden: z.number().int().min(1).max(36),
})
export type FrequentVerzuimConfigInput = z.infer<typeof frequentVerzuimConfigSchema>

/**
 * FO-387 — bron-marker in de plan-deeplink zodat het gesprek terugkoppelt naar
 * het signaal: `/hrm/agenda?plan=1&employeeId=&typeKey=VERZUIMGESPREK&signaalId=&bron=frequent-verzuim`.
 */
export const FREQUENT_VERZUIM_DEEPLINK_BRON = 'frequent-verzuim'

/** Eén meegetelde melding op het frequentie-blok (van-tot, geen inhoud). */
export interface VerzuimFrequentieMeldingDTO {
  id: string
  startDate: string // yyyy-MM-dd
  endDate: string | null
}

/**
 * FO-387 §3.6 — frequentie-blok op het verzuimdossier. Alleen aantallen +
 * periodes + verzuim% (nooit klachten). Wordt door de API alleen meegestuurd aan
 * een VERZUIM.canView-viewer; anders is het blok afwezig (FO-086-redactie).
 */
export interface VerzuimFrequentieBlokDTO {
  /** Erkende meldingen binnen het rolling window (drempel-predicaat). */
  aantalMeldingen: number
  periodeMaanden: number
  drempelAantal: number
  actief: boolean
  bovenDrempel: boolean
  /** BR #71-verzuim% over dezelfde periode; null zonder contract-info. */
  verzuimPct: number | null
  /** Het open (nog niet gekoppelde) signaal, indien aanwezig. */
  openSignaalId: string | null
  meldingen: VerzuimFrequentieMeldingDTO[]
}
